import axios from "axios";
import authService from "./auth";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export interface InviteRequest {
  fullName: string;
  email?: string;
  phone?: string;
  inviteChannelPref: "whatsapp" | "sms" | "email";
  tagIds?: string[];
}

export interface InviteResponse {
  employeeId: string;
  status: string;
  secureLinkSent: boolean;
  verificationLink: string;
  verificationToken: string;
  expiresAt: string;
}

class EmployeeService {
  private getAuthHeader(): string | null {
    return authService.getAuthHeader();
  }

  async sendInvitation(inviteData: InviteRequest): Promise<InviteResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/employees/invite`,
        inviteData,
        {
          headers: {
            Authorization: this.getAuthHeader(),
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to send invitation"
      );
    }
  }

  async getEmployeeList(
    status?: string,
    query?: string,
    limit: number = 100,
    cursor?: string,
    tagIds?: string[],
    page?: number,
    pageSize?: number
  ) {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (query) params.append("q", query);
      if (limit && !page) params.append("limit", String(limit));
      if (cursor) params.append("cursor", cursor);
      if (tagIds && tagIds.length > 0) params.append("tagIds", tagIds.join(","));
      if (page) params.append("page", String(page));
      if (pageSize) params.append("pageSize", String(pageSize));

      const response = await axios.get(
        `${API_BASE_URL}/employees?${params.toString()}`,
        {
          headers: {
            Authorization: this.getAuthHeader(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch employees"
      );
    }
  }

  async getEmployeeTags(employeeId: string) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/tags`,
        {
          headers: {
            Authorization: this.getAuthHeader(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch employee tags"
      );
    }
  }

  async updateEmployeeTags(employeeId: string, tagIds: string[]) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/employees/${employeeId}/tags`,
        { tagIds },
        {
          headers: {
            Authorization: this.getAuthHeader(),
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update employee tags"
      );
    }
  }

  async getEmployeeDetail(employeeId: string) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}`,
        {
          headers: {
            Authorization: this.getAuthHeader(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch employee details"
      );
    }
  }

  async sendBulkInvitation(invitations: InviteRequest[]): Promise<{
    bulkJobId: string;
    success: number;
    failed: number;
    total: number;
  }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/employees/bulk`,
        { rows: invitations },
        {
          headers: {
            Authorization: this.getAuthHeader(),
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to send bulk invitations"
      );
    }
  }
}

const employeeService = new EmployeeService();
export default employeeService;
